/*
 * blogsAction.js
 * trt_cms2
 *
 * Created by Hisyam on 27/2/2020.
 * Copyright (c) 2020 Terato Tech. All rights reserved.
 */

import moment from 'moment';
import {ofType} from "redux-observable";
import {forkJoin, of} from "rxjs";
import {catchError, debounceTime, map, mapTo, switchMap, withLatestFrom, pluck} from "rxjs/operators";
import {ajax} from "rxjs/ajax";
import {
  ADD_POST,
  ADD_POST_CATEGORY_FROM_SELECT,
  ADD_POST_CATEGORY_FROM_SELECT_FULFILLED,
  ADD_POST_FULFILLED, ADD_POST_TAG_FROM_SELECT,
  ADD_POST_TAG_FROM_SELECT_FULFILLED,
  DELETE_POST,
  DELETE_POST_FULFILLED,
  FETCH_POST,
  FETCH_POST_FULFILLED,
  FETCH_POSTS,
  FETCH_POSTS_FULFILLED,
  NEW_POST,
  NEW_POST_FULFILLED,
  RESET_REDIRECT_POST,
  UPDATE_POST,
  UPDATE_POST_FULFILLED
} from "../types";
import {returnErrors} from "../messages/messagesAction";
import {BLOG_POSTS_API, POST_CATEGORIES_API, POST_TAGS_API, USERS_API} from "../../config/api";
import {apiHeader, encodeQueryData} from "../../config/helper";

export const fetchPosts = (limit, offset, params={}) => ({type: FETCH_POSTS, payload: {limit: limit, offset: offset, ...params}});
const fetchPostsFulfilled = (payload) => ({type: FETCH_POSTS_FULFILLED, payload: payload});
export const fetchPostsEpic = (action$, state$) => action$.pipe(
  ofType(FETCH_POSTS),
  debounceTime(500),
  withLatestFrom(state$),
  switchMap(
    ([action, state]) => ajax.getJSON(BLOG_POSTS_API + `?${encodeQueryData(action.payload)}`, apiHeader(state))
      .pipe(
        map(fetchPostsFulfilled),
        catchError(({status, response}) => of(returnErrors(response, status)))
      )
  )
);

const categoriesAjax$ = state => ajax.getJSON(POST_CATEGORIES_API, apiHeader(state))
  .pipe(
    map(response => response.map(category => ({
      value: category.id,
      label: category.name
    })))
  );
const tagsAjax$ = state => ajax.getJSON(POST_TAGS_API, apiHeader(state))
  .pipe(
    map(response => response.map(tag => ({
      value: tag.id,
      label: tag.name
    })))
  );

export const newPost = () => ({type: NEW_POST});
const newPostFulfilled = payload => ({type: NEW_POST_FULFILLED, payload});
export const newPostEpic = (action$, state$) => action$.pipe(
  ofType(NEW_POST),
  withLatestFrom(state$),
  switchMap(([action, state]) => forkJoin({
    categories: categoriesAjax$(state),
    tags: tagsAjax$(state),
    author: ajax.getJSON(USERS_API, apiHeader(state))
  }).pipe(
    map(response => newPostFulfilled(response)),
    catchError(({response, status}) => of(returnErrors(response, status)))
  ))
);

export const fetchPost = id => ({type: FETCH_POST, payload: id});
const fetchPostFulfilled = payload => ({type: FETCH_POST_FULFILLED, payload});
export const fetchPostEpic = (action$, state$) => action$.pipe(
  ofType(FETCH_POST),
  withLatestFrom(state$),
  switchMap(([action, state]) => forkJoin({
    post: ajax.getJSON(BLOG_POSTS_API + `${action.payload}/`, apiHeader(state)),
    categories: categoriesAjax$(state),
    tags: tagsAjax$(state),
    author: ajax.getJSON(USERS_API, apiHeader(state))
  }).pipe(
    map(response => fetchPostFulfilled(response)),
    catchError(({response, status}) => of(returnErrors(response, status)))
  ))
);

export const addPost = (post) => ({type: ADD_POST, payload: post});
const addPostFulfilled = (payload) => ({type: ADD_POST_FULFILLED, payload});
export const addPostEpic = (action$, state$) => action$.pipe(
  ofType(ADD_POST),
  withLatestFrom(state$),
  switchMap(([action, state]) => ajax.post(BLOG_POSTS_API, action.payload, apiHeader(state))
    .pipe(
      pluck('response'),
      map(addPostFulfilled),
      catchError(({response, status}) => of(returnErrors(response, status)))
    )
  )
);

export const updatePost = (id, post) => ({type: UPDATE_POST, payload: post, id});
const updatePostFulfilled = payload => ({type: UPDATE_POST_FULFILLED, payload});
export const updatePostEpic = (action$, state$) => action$.pipe(
  ofType(UPDATE_POST),
  debounceTime(500),
  withLatestFrom(state$),
  switchMap(([action, state]) => ajax.put(BLOG_POSTS_API + `${action.id}/`, action.payload, apiHeader(state))
    .pipe(
      pluck('response'),
      map(updatePostFulfilled),
      catchError(({response, status}) => of(returnErrors(response, status)))
    )
  )
);

export const deletePost = id => ({type: DELETE_POST, id});
const deletePostFulfilled = id => ({type: DELETE_POST_FULFILLED, payload: id});
export const deletePostEpic = (action$, state$) => action$.pipe(
  ofType(DELETE_POST),
  debounceTime(500),
  withLatestFrom(state$),
  switchMap(
    ([action, state]) => ajax.delete(BLOG_POSTS_API + `${action.id}/`, apiHeader(state))
      .pipe(
        map(() => deletePostFulfilled(action.id)),
        catchError(({response, status}) => of(returnErrors(response, status)))
      )
  )
);

export const resetRedirectPostEpic = action$ => action$.pipe(
  ofType(ADD_POST_FULFILLED, UPDATE_POST_FULFILLED, DELETE_POST_FULFILLED),
  mapTo({type: RESET_REDIRECT_POST})
);


export const addPostCategory = name => ({type: ADD_POST_CATEGORY_FROM_SELECT, payload: {name}});
const addPostCategoryFulfilled = payload => ({type: ADD_POST_CATEGORY_FROM_SELECT_FULFILLED, payload});
export const addPostCategoryEpic = (action$, state$) => action$.pipe(
  ofType(ADD_POST_CATEGORY_FROM_SELECT),
  debounceTime(500),
  withLatestFrom(state$),
  switchMap(
    ([action, state]) => ajax.post(POST_CATEGORIES_API, action.payload, apiHeader(state))
      .pipe(
        pluck('response'),
        map(response => addPostCategoryFulfilled({value: response.id, label: response.name})),
        catchError(({response, status}) => of(returnErrors(response, status)))
      )
  )
);

export const addPostTag = name => ({type: ADD_POST_TAG_FROM_SELECT, payload: {name}});
const addPostTagFulfilled = payload => ({type: ADD_POST_TAG_FROM_SELECT_FULFILLED, payload});
export const addPostTagEpic = (action$, state$) => action$.pipe(
  ofType(ADD_POST_TAG_FROM_SELECT),
  debounceTime(500),
  withLatestFrom(state$),
  switchMap(
    ([action, state]) => ajax.post(POST_TAGS_API, action.payload, apiHeader(state))
      .pipe(
        pluck('response'),
        map(response => addPostTagFulfilled({value: response.id, label: response.name})),
        catchError(({response, status}) => of(returnErrors(response, status)))
      )
  )
);